import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { vendorService } from '../../../services/apiService';
import { useToast } from '../../../components/ui/Toast';
import { getCountryName, isIndia } from '../../../utils/countries';
import {
  formatSupplierCategory,
  formatSupplierType,
  formatMsmeStatus,
  formatBusinessVertical,
  formatIndustrySector,
  formatSupplierGroup,
  formatEmployeeCount,
  formatAnnualTurnover
} from '../../../utils/formatters';

const CompanyDetailsTab = ({ vendor, userRole, onVendorUpdate, approvalHistory = [] }) => {
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editableVendor, setEditableVendor] = useState(vendor);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to ensure website URL has proper protocol
  const getWebsiteUrl = (website) => {
    if (!website) return '';
    if (website.startsWith('http://') || website.startsWith('https://')) {
      return website;
    }
    return `https://${website}`;
  };

  // Helper functions to format questionnaire values
  const formatPaymentTerms = (value) => {
    if (!value) return 'Not specified';
    const paymentTermsMap = {
      '100ADVAOA': '100% Advance',
      '100AFDEL': '100% afer delivery',
      '105DAYSI': '105 DAYS FROM INV DATE',
      '10ADB0AD10': '10% Adv 80% against despatch & 10% balance after installati',
      '10ADB60DLC': '10% advance balance 60 days LC',
      '10ADBBS': '10% Advance Balance Before Shipment',
      '10ADTLC40T': '10% Adv Through LC , 40% on Trial zero Through LC , Balance',
      '10DAYS': '10 Days',
      '15DAYSI': '15 DAYS from Invoice',
      '180DAYSI': '180 Days',
      '20AD50Trial zero30': '20% Advance, 50% on Trial zero, Balance before dispatch',
      '20AD70AD10': '20% Adv 70% against despatch & 10% balance after installati',
      '20ADB80LC1Y': '20% advance and balance 80 % on one year LC against Bill of Landing',
      '20ADBBD': '20% Advance, Balance before dispatch',
      '20ADTLC30T': '20% Adv Through LC , 30% on Trial zero Through LC , Balance',
      '20ADV80SD': '20% Advance and 80% against shipping documents',
      '20ADVBOD': '20% Advance and Balance On Delivery',
      '25ADB45PDC': '25% Advance Balance 45Days PDC Before Dispatch',
      '25ADB60D': '25% ADVANCE BALANCE AFTER 60DAYS',
      '30AD60A80W': '30 % Adv, 60% after 80% work Done & 10% balance after Sucuss',
      '30AD60Trial zero10': '30% Advance, 60% on Trial zero and 10% After delivery within',
      '30AD70LC180BL': '30% Advance and 70% at 180 Days LC from Bill of Landing',
      '30ADVBABL': '30% adv, Balance Against BL copy',
      '30ADVBBD': '30% Advance Balance Before Dispatch',
      '35AD55BD10': '35% Advance , 55% before Dispatch , 10% After delivery in 30',
      '35DAYS': '35 Days',
      '40AD40A80W': '40% Adv, 40% after 80% work Done & 20% balance after Sucuss',
      '40AD40AD20': '40% Adv 40% against despatch & 20% balance after installat',
      '40AD60BD': '40% Advance 60% Before delivery',
      '50AD10Trial zero10': '50% Advance, 40% on Trial zero and 10% After delivery within',
      '50AD50LC180BL': '50% Advance and 50% at 180 Days LC from Bill of Landing',
      '50ADBA30DRM': '50 % advance and 50 % after 30 Days after receipt of Material',
      '50ADBABL': '50%Advance & balance against BL',
      '50ADVB180DLC': '50% advance and balance via 180 days LC',
      '50ADVB45DP': '50% ADV And Balance PDC of 45 DAYS',
      '5AD85AD10': '5% Adv 85% against despatch & 10% balance after installatio',
      '5ADTLC45Trial zero': '5% Adv Through LC , 45% on Trial zero Through LC , Balance 5',
      '7DAYS': '7 Days',
      '80AD20ACW': '80% Advance, 20% After Complition of Work',
      'AGSTDEL': 'Against Delivery',
      'BAGA20D': 'Basic amount Advance & GST tax amount after 2weeks',
      'IMMEDIATE': 'Immediate',
      'LC120DAYBL': 'LC 120 Days from date of BL',
      'LC150DAYBL': 'LC 150 Days from date of BL',
      'LC180DAYBL': 'LC 180 Days from date of BL',
      'LC30DAYSBL': '30 Days LC',
      'LC45DAYSBL': '45 Days LC From Bill of Lading',
      'LC45DAYSI': 'LC 45 Days from date of Invoice',
      'LC60DAYSBL': 'LC 60 Days from date of BL',
      'LC60DAYSI': 'LC 60 Days from date of Invoice',
      'LC75DAYSI': 'LC 75 Days from date of Invoice',
      'LC90DAYSBL': 'LC 90 Days from date of BL',
      'LC90DAYSI': 'LC 90 Days from date of Invoice',
      'LCATSIGHT': 'LC AT SITE',
      'OA120DASBL': 'OA-120 Days from date of BL',
      'OA120DAYSI': 'OA-120 Days from date of Invoice',
      'OA150DASBL': 'OA-150 Days from date of BL',
      'OA180DAYBL': 'OA-180 Days from date of BL',
      'OA20ADVAN': '20% Advance, Balance under satisfactory completeion of work',
      'OA30ADVAN': '30% Advance, Balance under satisfactory completeion of work',
      'OA30DAYSI': 'OA 30 Days from date of Invoice',
      'OA45DAYSBL': 'OA- 45 Days from date of BL',
      'OA45DAYSI': 'OA- 45 Days from date of Invocie',
      'OA50ADVAN': '50% Advance, Balance under satisfactory completeion of work',
      'OA60DAYSBL': 'OA 60 Days from date of BL',
      'OA60DAYSI': 'OA- 60 Days from date of Invoice',
      'OA75ADVAN': '75% Advance, Balance under satisfactory completeion of work',
      'OA75DAYSBL': 'OA-75 Days from date of BL',
      'OA75DAYSI': 'OA-75 Days from date of Invoice',
      'OA7DAYSI': 'OA 7 Days from date of Invoice',
      'OA90DAYSBL': 'OA- 90 Days from date of BL',
      'OA90DAYSI': 'OA- 90 Days from date of Invoice',
      'PDC30DAYS': 'PDC 30 Days',
      'PDC45DAYS': 'PDC 45 Days',
      'PDC60DAYS': 'PDC 60 Days',
      'PDC75DAYS': 'PDC 75 Days',
      'TT105DBL': 'TT 105 days from Date of Bill of Lading',
      'TTAdvance': 'TT Advance',
      'TTAGTBL': '100% TT Against BL Copy'
    };
    return paymentTermsMap[value] || value;
  };

  const formatPaymentMethod = (value) => {
    if (!value) return 'Not specified';
    const paymentMethodMap = {
      'CASH': 'Cash',
      'CHEQUE': 'Cheque',
      'NEFT/RTGS': 'NEFT/RTGS',
      'TT': 'TT',
      'LC': 'LC',
      'PDC': 'PDC'
    };
    return paymentMethodMap[value] || value;
  };

  const formatDeliveryTerms = (value) => {
    if (!value) return 'Not specified';
    const deliveryTermsMap = {
      'AS PER SCH': 'AS PER SCH',
      'CIF': 'CIF',
      'EXTRA': 'EXTRA',
      'EXW': 'EXW',
      'EXWDDN': 'EXWDDN',
      'FOB': 'FOB',
      'FOR': 'FOR',
      'IN': 'IN',
      'LCL': 'LCL',
      'TO PAID': 'TO PAID',
      'TO PAY': 'TO PAY'
    };
    return deliveryTermsMap[value] || value;
  };

  const formatDeliveryMode = (value) => {
    if (!value) return 'Not specified';
    const deliveryModeMap = {
      'BY AIR': 'By Air',
      'BY COURIER': 'By Courier',
      'BY HAND': 'By Hand',
      'BY ROAD': 'By Road',
      'BY SEA': 'By Sea'
    };
    return deliveryModeMap[value] || value;
  };

  const formatSupplierGroup = (value) => {
    if (!value) return 'Not specified';
    const supplierGroupMap = {
      // Vendor registration supplier groups
      'oem-customer': 'OEM-Customer Referred Supplier',
      'odm-amber': 'ODM-PICLDeveloped Supplier',
      
      // Questionnaire supplier groups
      'CR-AGT': 'CR-AGT | Creditors Agents',
      'CR-BANK': 'CR-BANK | Creditors Bank',
      'CR-CAP': 'CR-CAP | Creditors Capex',
      'CR-CO': 'CR-CO | Creditors Consumable',
      'CR-EX': 'CR-EX | Creditors Expense',
      'CR-INT': 'CR-INT | Creditors Inter Unit',
      'CR-JW': 'CR-JW | Creditors Job work',
      'CR-KMP': 'CR-KMP | Creditors Key Management Persons',
      'CR-PK': 'CR-PK | Creditors Packing Material',
      'CR-RM': 'CR-RM | Creditors Raw Material',
      'CR-RPT': 'CR-RPT | Creditors Related Parties',
      'CR-SC': 'CR-SC | Creditors Sister Concern',
      'CR-IMP': 'CR-IMP | Creditors Imports Raw Material',
      'CR-IMP-CAP': 'CR-IMP-CAP | Creditors Import Capex',
      'CR-IMP-EXP': 'CR-IMP-EXP | Creditors Import Expense',
      'CR-IMP-SER': 'CR-IMP-SER | Creditors Import Service'
    };
    return supplierGroupMap[value] || value;
  };

  const formatCommodityCode = (value) => {
    if (!value) return 'Not specified';
    const commodityCodeMap = {
      'AA': 'AA - Aluminium',
      'AB': 'AB - FG-Extruded Sheet',
      'AC': 'AC - FG-HE Coil',
      'AD': 'AD - FG-IDU',
      'AE': 'AE - FG-Inner Case',
      'AF': 'AF - FG-MFC',
      'AG': 'AG - FG-ODU',
      'AH': 'AH - FG-PLC',
      'AI': 'AI - FG-SMC',
      'AJ': 'AJ - Steel',
      'AK': 'AK - FG-WAC',
      'AL': 'AL - FG-ODU Kit',
      'AM': 'AM - FG-SAC',
      'AN': 'AN - ODU-Accessories',
      'AO': 'AO - Compressor',
      'AP': 'AP - Motor',
      'AQ': 'AQ - RM-IDU',
      'AR': 'AR - RM-HE Coil',
      'AS': 'AS - Sticker & Label',
      'AT': 'AT - Remote',
      'AU': 'AU - Foam',
      'AV': 'AV - EPS',
      'AX': 'AX - Brass Parts',
      'AY': 'AY - Capacitor',
      'AZ': 'AZ - Cross Flow Fan',
      'BA': 'BA - FG-Portable AC',
      'BB': 'BB - Fan',
      'BC': 'BC - Gas',
      'BD': 'BD - Hardware',
      'BE': 'BE - Ink',
      'BF': 'BF - Poly Bag',
      'BG': 'BG - Rear Grill',
      'BH': 'BH - Hardware Other',
      'BI': 'BI - Rubber Parts',
      'BJ': 'BJ - Service Valve',
      'BK': 'BK - Tape',
      'BL': 'BL - Wire',
      'BM': 'BM - FG-PP Roll',
      'BN': 'BN - Bolt',
      'BO': 'BO - Brazing Rod',
      'BP': 'BP - Carton Box',
      'BQ': 'BQ - Chemical',
      'BR': 'BR - FG-Copper Tubing',
      'BS': 'BS - Powder',
      'BT': 'BT - Terminal Block',
      'BU': 'BU - Packing Material',
      'BV': 'BV - Copper Tube'
    };
    return commodityCodeMap[value] || value;
  };

  // Extract questionnaire data from approval history
  const getQuestionnaireData = () => {
    const questionnaireEntry = approvalHistory.find(event => 
      event.comments && event.comments.includes('Questionnaire completed:')
    );

    if (!questionnaireEntry) {
      return {};
    }

    // Extract questionnaire data from comments
    const questionnaireString = questionnaireEntry.comments.replace('Questionnaire completed: ', '');
    const questionnairePairs = questionnaireString.split(', ');
    
    // Parse the questionnaire data
    const questionnaireData = {};
    questionnairePairs.forEach(pair => {
      const [key, value] = pair.split(': ');
      if (key && value) {
        questionnaireData[key] = value;
      }
    });

    return questionnaireData;
  };

  const questionnaireData = getQuestionnaireData();

  // Update editableVendor when vendor prop changes
  React.useEffect(() => {
    setEditableVendor(vendor);
  }, [vendor]);

  const companyDetails = {
    basicInfo: {
      title: 'Basic Information',
      icon: 'Building2',
      fields: [
        { label: 'Company Name', value: editableVendor?.company_name || 'N/A', key: 'company_name' },
        { label: 'Business Vertical', value: formatBusinessVertical(editableVendor?.business_vertical), key: 'business_vertical' },
        { label: 'Year Established', value: editableVendor?.year_established || 'N/A', key: 'year_established' },
        { label: 'Business Description', value: editableVendor?.business_description || 'N/A', key: 'business_description' }
      ]
    },
    registrationInfo: {
      title: 'Registration Information',
      icon: 'FileText',
      fields: [
        { label: 'Country of Origin', value: getCountryName(editableVendor?.country_origin) || 'N/A', key: 'country_origin' },
        ...(isIndia(editableVendor?.country_origin) ? [
          { label: 'PAN Number', value: editableVendor?.pan_number || 'N/A', key: 'pan_number' },
          { label: 'GST Number', value: editableVendor?.gst_number || 'N/A', key: 'gst_number' }
        ] : [
          { label: 'Tax Registration Number', value: editableVendor?.tax_registration_number || 'N/A', key: 'tax_registration_number' },
          { label: 'VAT/Sales Tax Number', value: editableVendor?.vat_number || 'N/A', key: 'vat_number' },
          { label: 'Business License Number', value: editableVendor?.business_license || 'N/A', key: 'business_license' }
        ]),
        { label: 'MSME Status', value: formatMsmeStatus(editableVendor?.msme_status), key: 'msme_status' },
        { label: 'MSME Number', value: editableVendor?.msme_number || 'N/A', key: 'msme_number' },
        { 
          label: 'Classification Date', 
          value: editableVendor?.classification_date 
            ? new Date(editableVendor.classification_date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })
            : 'N/A', 
          key: 'classification_date' 
        },
        { label: 'Classification Year', value: editableVendor?.classification_year || 'N/A', key: 'classification_year' }
      ]
    },
    addressInfo: {
      title: 'Address Information',
      icon: 'MapPin',
      fields: [
        { label: 'Registered Address', value: editableVendor?.registered_address || 'N/A', key: 'registered_address', multiline: true },
        { label: 'Registered City', value: editableVendor?.registered_city || 'N/A', key: 'registered_city' },
        { label: 'Registered State', value: editableVendor?.registered_state || 'N/A', key: 'registered_state' },
        { label: 'Registered Country', value: editableVendor?.registered_country || 'N/A', key: 'registered_country' },
        { label: 'Registered Pincode', value: editableVendor?.registered_pincode || 'N/A', key: 'registered_pincode' },
        { label: 'Supply Address', value: editableVendor?.supply_address || 'N/A', key: 'supply_address', multiline: true },
        { label: 'Supply City', value: editableVendor?.supply_city || 'N/A', key: 'supply_city' },
        { label: 'Supply State', value: editableVendor?.supply_state || 'N/A', key: 'supply_state' },
        { label: 'Supply Country', value: editableVendor?.supply_country || 'N/A', key: 'supply_country' },
        { label: 'Supply Pincode', value: editableVendor?.supply_pincode || 'N/A', key: 'supply_pincode' }
      ]
    },
    businessInfo: {
      title: 'Business Information',
      icon: 'Briefcase',
      fields: [
        { label: 'Employee Count', value: formatEmployeeCount(editableVendor?.employee_count), key: 'employee_count' },
        { label: 'Annual Turnover', value: formatAnnualTurnover(editableVendor?.annual_turnover), key: 'annual_turnover' },
        { label: 'Supplier Group', value: formatSupplierGroup(editableVendor?.supplier_group), key: 'supplier_group' },
        { label: 'Supplier Category', value: formatSupplierCategory(editableVendor?.supplier_category), key: 'supplier_category' },
      ]
    },
    paymentInfo: {
      title: 'Payment Information',
      icon: 'CreditCard',
      fields: [
        { label: 'Terms of Payment', value: formatPaymentTerms(questionnaireData.supplierTermOfPayment), key: 'supplier_term_of_payment' },
        { label: 'Payment Method', value: formatPaymentMethod(questionnaireData.supplierPaymentMethod), key: 'supplier_payment_method' },
      ]
    },
    deliveryInfo: {
      title: 'Delivery Information',
      icon: 'Truck',
      fields: [
        { label: 'Delivery Terms', value: formatDeliveryTerms(questionnaireData.supplierDeliveryTerms), key: 'supplier_delivery_terms' },
        { label: 'Mode of Delivery', value: formatDeliveryMode(questionnaireData.supplierModeOfDelivery), key: 'supplier_mode_of_delivery' },
      ]
    },
    supplierClassification: {
      title: 'Supplier Classification',
      icon: 'Tag',
      fields: [
        { label: 'Supplier Group', value: formatSupplierGroup(questionnaireData.supplierGroup), key: 'supplier_group' },
        { label: 'Commodity Code', value: formatCommodityCode(questionnaireData.commodityCode), key: 'commodity_code' },
      ]
    }
  };

  const handleInputChange = (key, value) => {
    setEditableVendor(prev => ({ ...prev, [key]: value }));
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorService.updateVendor(vendor.id, editableVendor);
      const updatedVendor = response?.data ?? response;
      if (onVendorUpdate) {
        onVendorUpdate(updatedVendor);
      }
      setIsEditing(false);
      toast.success('Vendor details updated successfully!');
    } catch (err) {
      setError(`Failed to save changes: ${err.message}`);
      console.error('Error saving vendor details:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    if (field.multiline) {
      return (
        <div key={field.key} className="space-y-2">
          <label className="text-sm font-medium text-slate-600">{field.label}:</label>
          <div className="bg-slate-50 p-3 rounded-md border border-slate-200">
            <p className="text-sm text-slate-900 whitespace-pre-wrap">{field.value}</p>
          </div>
        </div>
      );
    }

    return (
      <div key={field.key} className="flex justify-between items-center py-2">
        <span className="text-slate-600">{field.label}:</span>
        <span className="font-medium text-slate-900 text-right">{field.value}</span>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Action Bar */}
      {/* {(userRole === 'Admin' || userRole === 'Approver') && (
        <div className="flex justify-end">
          {isEditing ? (
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button variant="default" onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          ) : (
            <Button variant="default" iconName="Edit" iconPosition="left" onClick={handleEdit}>
              Edit Details
            </Button>
          )}
        </div>
      )} */}

      {/* Error Display */}
      {error && (
        <Card className="border border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Company Details Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {Object.entries(companyDetails).map(([sectionKey, section]) => (
          <Card key={sectionKey} className="border border-slate-200 shadow-sm bg-white">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Icon name={section.icon} size={20} className="text-slate-600" />
                {section.title}
              </h3>
              <div className="space-y-3">
                {section.fields.map(field => (
                  <div key={field.key} className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-2">
                    <label className="text-sm font-medium text-slate-600 mb-1 sm:mb-0">{field.label}:</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editableVendor[field.key] || ''}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        className="flex-1 p-2 border border-slate-300 rounded-md text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-500"
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                    ) : (
                      <span className="font-medium text-slate-900 text-right">{field.value}</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Additional Information */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Icon name="Info" size={20} className="text-slate-600" />
            Additional Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Website:</span>
                {editableVendor?.website ? (
                  <a href={getWebsiteUrl(editableVendor.website)} target="_blank" rel="noopener noreferrer" 
                     className="font-medium text-blue-600 hover:underline">
                    {editableVendor.website}
                  </a>
                ) : (
                  <span className="font-medium text-slate-900">N/A</span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Email:</span>
                <span className="font-medium text-slate-900">{editableVendor?.email || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Phone:</span>
                <span className="font-medium text-slate-900">{editableVendor?.phone_number || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Contact Person:</span>
                <span className="font-medium text-slate-900">{editableVendor?.contact_person_name || 'N/A'}</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Designation:</span>
                <span className="font-medium text-slate-900">{editableVendor?.designation || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Credit Rating:</span>
                <span className="font-medium text-slate-900">{editableVendor?.credit_rating || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Insurance Coverage:</span>
                <span className="font-medium text-slate-900">{editableVendor?.insurance_coverage || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">GTA (Goods Transport Agency) Registration:</span>
                <span className="font-medium text-slate-900">{editableVendor?.gta_registration || 'N/A'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanyDetailsTab;